import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BlockDraftWorkoutDomain } from '@strength/domain';
import { trpc } from '../../trpc/react';
import { toast } from './useToast';

export type WorkoutMoveOutcome = 'moved' | 'no-op' | 'outside-block';

export type MoveWorkoutDateArgs = {
  blockId: string;
  blockStartDate: string;
  blockEndDate: string;
  workoutId: string;
  currentAssignedDate: string | null;
  newAssignedDate: string;
  /** Set when the workout lives on an athlete layer (selected-athlete view). */
  profileId?: string | null;
};

/**
 * The one write path both workout-drag surfaces (Annual Planner Calendar and
 * the block builder All view) share: the assigned date changes, nothing else
 * about the workout's place in the block does.
 *
 * Semantics owned here so both surfaces behave identically:
 * - a drop on the workout's current date is a no-op (no toast, no save);
 * - a drop outside the block's start/end dates is rejected with the
 *   "Workout must stay within block dates" error toast and no save;
 * - a server rejection for logged actuals (the workout is in progress)
 *   warns with the same "can't be moved" toast the drag surfaces show;
 * - a save shows a saving state, confirms with the Workout Updated toast,
 *   and on failure shows an error toast (the write is server-side, so a
 *   failure leaves the workout exactly where it was).
 */
export function useMoveWorkoutDate() {
  const { t } = useTranslation();
  const [movingWorkoutIds, setMovingWorkoutIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const mutation = trpc.block.draftWorkout.moveWorkoutDate.useMutation();

  const moveWorkoutDate = useCallback(
    (args: MoveWorkoutDateArgs): WorkoutMoveOutcome => {
      // Same-date drop: a no-op — no toast, no save.
      if (args.newAssignedDate === args.currentAssignedDate) {
        return 'no-op';
      }

      // Date-only ISO strings compare correctly lexicographically.
      if (
        args.newAssignedDate < args.blockStartDate ||
        args.newAssignedDate > args.blockEndDate
      ) {
        toast({
          title: t('workoutMustStayWithinBlockDates'),
          severity: 'error',
        });
        return 'outside-block';
      }

      const workoutId = args.workoutId;
      setMovingWorkoutIds((prev) => new Set(prev).add(workoutId));
      mutation.mutate(
        {
          blockId: args.blockId,
          workoutId: args.workoutId,
          profileId: args.profileId ?? null,
          assignedDate: args.newAssignedDate,
        },
        {
          onSuccess: () => {
            toast({ title: t('workoutUpdated') });
          },
          onError: (error) => {
            // The write is server-side: a failure means the date was never
            // changed, so there is nothing to revert — only the toast. A
            // logged-actuals rejection means the workout was already started
            // (the client's actuals view can lag the server): surface the
            // same warning the drag surfaces show, not a generic error.
            if (
              error.message ===
              BlockDraftWorkoutDomain.WORKOUT_HAS_LOGGED_ACTUALS_ERROR
            ) {
              toast({
                title: t('workoutCannotBeMovedInProgress'),
                severity: 'warning',
              });
              return;
            }
            toast({ title: t('somethingWentWrong'), severity: 'error' });
          },
          onSettled: () => {
            setMovingWorkoutIds((prev) => {
              const next = new Set(prev);
              next.delete(workoutId);
              return next;
            });
          },
        },
      );
      return 'moved';
    },
    [mutation, t],
  );

  const isWorkoutMoving = useCallback(
    (workoutId: string) => movingWorkoutIds.has(workoutId),
    [movingWorkoutIds],
  );

  return { moveWorkoutDate, isWorkoutMoving };
}
